"use client";

import { isToday, isYesterday, subMonths, subWeeks } from "date-fns";
import { useParams, useRouter } from "next/navigation";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API_BASE_URL } from "@/lib/config";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar";
import type { Chat } from "@/lib/db/schema";
import { ChatItem } from "./sidebar-history-item";
import useSWRInfinite from "swr/infinite";
import { LoaderIcon } from "./icons";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveSessions } from "@/hooks/use-active-sessions";
import { telemetry } from "@/lib/telemetry";

type GroupedChats = {
  today: Chat[];
  yesterday: Chat[];
  lastWeek: Chat[];
  lastMonth: Chat[];
  older: Chat[];
};

export interface ChatHistory {
  chats: Array<Chat>;
  hasMore: boolean;
}

const PAGE_SIZE = 20;

// Custom fetcher for the external backend
const customSessionsFetcher = async (url: string): Promise<ChatHistory> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Failed to fetch sessions");
  }

  const data = await response.json();
  // Filter sessions to only include those with mode=chat (extra safety measure)
  const chatSessions = data.sessions.filter(
    (session: any) => session.mode === "chat" || session.mode === "Chat"
  );

  // Transform backend response to match expected ChatHistory format
  const transformedChats = chatSessions.map((session: any) => ({
    id: session.session_id,
    title: session.title || "New Chat",
    visibility: "private" as const,
    userId: session.user_id,
    createdAt: new Date(session.created_at),
    is_favorited: session.is_favorited || false,
  }));

  return {
    chats: transformedChats,
    hasMore: transformedChats.length === PAGE_SIZE, // More pages if we got full page size
  };
};

const groupChatsByDate = (chats: Chat[]): GroupedChats => {
  const now = new Date();
  const oneWeekAgo = subWeeks(now, 1);
  const oneMonthAgo = subMonths(now, 1);

  return chats.reduce(
    (groups, chat) => {
      const chatDate = new Date(chat.createdAt);

      if (isToday(chatDate)) {
        groups.today.push(chat);
      } else if (isYesterday(chatDate)) {
        groups.yesterday.push(chat);
      } else if (chatDate > oneWeekAgo) {
        groups.lastWeek.push(chat);
      } else if (chatDate > oneMonthAgo) {
        groups.lastMonth.push(chat);
      } else {
        groups.older.push(chat);
      }

      return groups;
    },
    {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: [],
    } as GroupedChats
  );
};

export function getChatHistoryPaginationKey(
  pageIndex: number,
  previousPageData: ChatHistory,
  userId?: string,
  sortBy: "all" | "favorites" = "all"
) {
  if (!userId) return null;

  if (previousPageData && previousPageData.hasMore === false) {
    return null;
  }

  const skip = pageIndex * PAGE_SIZE;
  return `${API_BASE_URL}/sessions?user_id=${userId}&limit=${PAGE_SIZE}&skip=${skip}&mode=chat&sort_by=${sortBy}`;
}

export function SidebarHistory({ sortBy = "all" }: { sortBy?: "all" | "favorites" }) {
  const { setOpenMobile } = useSidebar();
  const { id } = useParams();
  const { user: firebaseUser } = useAuth();

  const {
    data: paginatedChatHistories,
    error: historyError,
    setSize,
    isValidating,
    isLoading,
    mutate,
  } = useSWRInfinite<ChatHistory>(
    (pageIndex, previousPageData) =>
      getChatHistoryPaginationKey(
        pageIndex,
        previousPageData,
        firebaseUser?.uid,
        sortBy
      ),
    customSessionsFetcher,
    {
      fallbackData: [],
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  const router = useRouter();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const chatIds = useMemo(() => {
    if (!paginatedChatHistories) return [];
    return paginatedChatHistories.flatMap((page) =>
      page.chats.map((c) => c.id)
    );
  }, [paginatedChatHistories]);

  const { getActivity } = useActiveSessions(chatIds);

  const hasReachedEnd = paginatedChatHistories
    ? paginatedChatHistories.some((page) => page.hasMore === false)
    : false;

  // Listen for load more event from parent scroll handler
  useEffect(() => {
    const handleLoadMore = () => {
      if (!isValidating && !hasReachedEnd) {
        setSize((size) => size + 1);
      }
    };

    window.addEventListener("loadMoreChats", handleLoadMore);
    return () => window.removeEventListener("loadMoreChats", handleLoadMore);
  }, [isValidating, hasReachedEnd, setSize, paginatedChatHistories]);

  const hasEmptyChatHistory = paginatedChatHistories
    ? paginatedChatHistories.every((page) => page.chats.length === 0)
    : false;

  useEffect(() => {
    if (historyError) {
      telemetry.emit("session.sidebar_fetch_error", {
        user_id: firebaseUser?.uid,
        sort_by: sortBy,
        error_message:
          historyError instanceof Error ? historyError.message.slice(0, 500) : String(historyError).slice(0, 500),
      });
    }
  }, [firebaseUser?.uid, historyError, sortBy]);

  useEffect(() => {
    if (!paginatedChatHistories || isLoading) return;
    const totalChats = paginatedChatHistories.reduce((sum, page) => sum + page.chats.length, 0);
    telemetry.emit("session.sidebar_loaded", {
      user_id: firebaseUser?.uid,
      sort_by: sortBy,
      page_count: paginatedChatHistories.length,
      total_chats: totalChats,
      has_reached_end: hasReachedEnd,
    });
  }, [firebaseUser?.uid, hasReachedEnd, isLoading, paginatedChatHistories, sortBy]);

  const handleDelete = async () => {
    telemetry.emit("session.delete_click", {
      session_id: deleteId || "unknown",
      user_id: firebaseUser?.uid,
      is_current_session: deleteId === id,
    });
    const deletePromise = fetch(`${API_BASE_URL}/sessions/${deleteId}`, {
      method: "DELETE",
    }).then(async (response) => {
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`DELETE ${response.status}: ${text.slice(0, 300)}`);
      }
      return response;
    });

    toast.promise(deletePromise, {
      loading: "Deleting chat...",
      success: () => {
        telemetry.emit("session.delete_success", {
          session_id: deleteId || "unknown",
          user_id: firebaseUser?.uid,
          is_current_session: deleteId === id,
        });
        mutate((chatHistories) => {
          if (chatHistories) {
            return chatHistories.map((chatHistory) => ({
              ...chatHistory,
              chats: chatHistory.chats.filter((chat) => chat.id !== deleteId),
            }));
          }
        });

        return "Chat deleted successfully";
      },
      error: (err) => {
        telemetry.emit("session.delete_error", {
          session_id: deleteId || "unknown",
          user_id: firebaseUser?.uid,
          error_message: err instanceof Error ? err.message.slice(0, 500) : String(err).slice(0, 500),
        });
        return "Failed to delete chat";
      },
    });

    setShowDeleteDialog(false);

    if (deleteId === id) {
      router.push("/");
    }
  };

  const handleRename = (chatId: string, newTitle: string) => {
    telemetry.emit("session.rename_click", {
      session_id: chatId,
      user_id: firebaseUser?.uid,
      title_length: newTitle.length,
    });
    // Optimistic update
    mutate((chatHistories) => {
      if (chatHistories) {
        return chatHistories.map((chatHistory) => ({
          ...chatHistory,
          chats: chatHistory.chats.map((chat) =>
            chat.id === chatId ? { ...chat, title: newTitle } : chat
          ),
        }));
      }
    }, false);

    // API call with toast
    toast.promise(
      fetch(`${API_BASE_URL}/sessions/${chatId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      }).then(async (response) => {
        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(`PATCH ${response.status}: ${text.slice(0, 300)}`);
        }
        return response;
      }),
      {
        loading: "Updating...",
        success: () => {
          telemetry.emit("session.rename_success", {
            session_id: chatId,
            user_id: firebaseUser?.uid,
            title_length: newTitle.length,
          });
          return "Updated successfully";
        },
        error: (err) => {
          telemetry.emit("session.rename_error", {
            session_id: chatId,
            user_id: firebaseUser?.uid,
            error_message: err instanceof Error ? err.message.slice(0, 500) : String(err).slice(0, 500),
          });
          mutate();
          return "Failed to update";
        },
      }
    );
  };

  const handleFavorite = async (chatId: string) => {
    // Get current favorite status before optimistic update
    const currentChat = paginatedChatHistories
      ?.flatMap((page) => page.chats)
      .find((chat) => chat.id === chatId);
    const wasFavorited = (currentChat as any)?.is_favorited || false;
    telemetry.emit("session.favorite_toggle", {
      session_id: chatId,
      user_id: firebaseUser?.uid,
      target_state: !wasFavorited,
      sort_by: sortBy,
    });

    // Optimistic update
    mutate((chatHistories) => {
      if (chatHistories) {
        return chatHistories.map((chatHistory) => ({
          ...chatHistory,
          chats: chatHistory.chats.map((chat) =>
            chat.id === chatId
              ? { ...chat, is_favorited: !(chat as any).is_favorited }
              : chat
          ),
        }));
      }
    }, false);

    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${chatId}/favorite`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Failed to update favorite status");

      // Show success message
      toast.success(wasFavorited ? "Removed from Favorites" : "Added to Favorites");
      telemetry.emit("session.favorite_success", {
        session_id: chatId,
        user_id: firebaseUser?.uid,
        target_state: !wasFavorited,
      });

      // Revalidate
      mutate();
    } catch (error) {
      // Revert on error
      mutate();
      telemetry.emit("session.favorite_error", {
        session_id: chatId,
        user_id: firebaseUser?.uid,
        target_state: !wasFavorited,
        error_message: error instanceof Error ? error.message.slice(0, 500) : String(error).slice(0, 500),
      });
      toast.error(wasFavorited ? "Failed to remove from Favorites" : "Failed to add to Favorites");
    }
  };

  const handleFork = (chatId: string) => {
    if (!firebaseUser?.uid) {
      toast.error("Please log in to fork a chat");
      return;
    }
    telemetry.emit("session.fork_click", {
      session_id: chatId,
      user_id: firebaseUser.uid,
      is_current_session: chatId === id,
    });

    const forkPromise = fetch(
      `${API_BASE_URL}/sessions/${chatId}/fork?requesting_user_id=${encodeURIComponent(
        firebaseUser.uid
      )}`,
      { method: "POST" }
    ).then(async (response) => {
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        // Backend returns { detail: "..." } for 403/404/409 — surface it verbatim.
        let message = `Fork failed (${response.status})`;
        try {
          const body = JSON.parse(text);
          if (body?.detail) message = body.detail;
        } catch {
          if (text) message = text.slice(0, 300);
        }
        throw new Error(message);
      }
      return response.json();
    });

    toast.promise(forkPromise, {
      loading: "Forking chat...",
      success: (data: any) => {
        const newSessionId = data?.session_id;
        telemetry.emit("session.fork_success", {
          session_id: chatId,
          user_id: firebaseUser.uid,
          new_session_id: newSessionId,
          message_count: data?.message_count,
        });
        // Revalidate the list so the new chat shows up at the top.
        mutate();
        if (newSessionId) {
          router.push(`/chat/${newSessionId}`);
        }
        return "Chat forked";
      },
      error: (err) => {
        telemetry.emit("session.fork_error", {
          session_id: chatId,
          user_id: firebaseUser.uid,
          error_message:
            err instanceof Error ? err.message.slice(0, 500) : String(err).slice(0, 500),
        });
        return err instanceof Error && err.message
          ? err.message
          : "Failed to fork chat";
      },
    });
  };

  if (!firebaseUser) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="px-2 text-zinc-500 w-full flex flex-row justify-center items-center text-sm gap-2">
            Login to save and revisit previous chats!
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (isLoading) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="flex flex-col">
            {[44, 32, 28, 64, 52].map((item) => (
              <div
                key={item}
                className="rounded-md h-8 flex gap-2 px-2 items-center"
              >
                <div
                  className="h-4 rounded-md flex-1 max-w-[--skeleton-width] bg-sidebar-accent-foreground/10"
                  style={
                    {
                      "--skeleton-width": `${item}%`,
                    } as React.CSSProperties
                  }
                />
              </div>
            ))}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (hasEmptyChatHistory) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="px-2 text-zinc-500 w-full flex flex-row justify-center items-center text-sm gap-2">
            {sortBy === "favorites" 
              ? "You haven't favorited any conversations yet! Click the ⋯ menu on any chat and select 'Favorite'."
              : "Your conversations will appear here once you start chatting!"}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <>
      <SidebarGroup className="pt-2">
        <SidebarGroupContent>
          <div>
            <SidebarMenu className={isValidating ? "pb-4" : ""}>
              {paginatedChatHistories &&
                (() => {
                  const chatsFromHistory = paginatedChatHistories.flatMap(
                    (paginatedChatHistory) => paginatedChatHistory.chats
                  );

                  return (
                    <div className="flex flex-col gap-2">
                      {chatsFromHistory.map((chat) => (
                        <ChatItem
                          key={chat.id}
                          chat={chat}
                          isActive={chat.id === id}
                          sessionActivity={getActivity(chat.id)}
                          onDelete={(chatId) => {
                            setDeleteId(chatId);
                            setShowDeleteDialog(true);
                          }}
                          onRename={handleRename}
                          onFavorite={handleFavorite}
                          onFork={handleFork}
                          setOpenMobile={setOpenMobile}
                        />
                      ))}
                    </div>
                  );
                })()}

              {isValidating && (
                <div className="space-y-2 px-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="pl-0 pr-2 py-2 rounded-md">
                      <div className="space-y-1">
                        <div className="h-3 bg-muted rounded animate-pulse" />
                        <div className="h-2 bg-muted/60 rounded animate-pulse w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SidebarMenu>
          </div>
        </SidebarGroupContent>
      </SidebarGroup>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              chat and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
